import { useParams } from 'react-router-dom';
import { Card, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { ToggleRow } from '@/components/ui/ToggleRow';
import { Alert } from '@/components/ui/Alert';
import { SaveBar } from '@/components/ui/SaveBar';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { useGuildConfig } from '@/hooks/useGuildConfig';
import { ModuleTogglePanel } from '@/components/dashboard/ModuleTogglePanel';

/** Matches guild_prefixes/utils/prefix.py's own validation exactly —
 * bridge/config_registry.py's validate_prefix() is the real enforcement;
 * this is just an early, friendlier hint in the UI. */
const MAX_PREFIX_LENGTH = 5;

interface PrefixData {
  prefix: string;
}

function PrefixCard() {
  const { guildId } = useParams<{ guildId: string }>();
  const { state, current, dirty, saving, saveError, resetting, update, save, discard, reset } =
    useGuildConfig<PrefixData>(guildId, 'prefix');

  if (state.kind === 'loading') {
    return (
      <Card>
        <CardHeader title="Command prefix" description="Used for text commands. Slash commands always show / regardless." />
        <Skeleton className="h-9 w-32" />
      </Card>
    );
  }

  if (state.kind === 'forbidden') {
    return (
      <Card>
        <CardHeader title="Command prefix" />
        <Alert tone="danger" title="You don't have permission to view this" description="Changing the prefix requires Administrator." />
      </Card>
    );
  }

  if (state.kind === 'unavailable' || !current) {
    return (
      <Card>
        <CardHeader title="Command prefix" />
        <Alert tone="danger" title="Couldn't load the prefix" description={state.kind === 'unavailable' ? state.message : 'Something went wrong.'} />
      </Card>
    );
  }

  const prefixError = current.prefix.length > MAX_PREFIX_LENGTH ? `Max ${MAX_PREFIX_LENGTH} characters.` : undefined;

  return (
    <Card>
      <CardHeader title="Command prefix" description="Used for text commands. Slash commands always show / regardless." />
      {saveError && <div className="mb-3"><Alert tone="danger" title="Couldn't save" description={saveError} /></div>}
      <div className="flex items-end gap-3">
        <div className="w-32">
          <Input
            value={current.prefix}
            maxLength={MAX_PREFIX_LENGTH}
            error={prefixError}
            onChange={(e) => update({ prefix: e.target.value })}
            aria-label="Command prefix"
          />
        </div>
        <Button variant="ghost" size="md" onClick={reset} disabled={resetting}>
          {resetting ? 'Resetting…' : 'Reset to default'}
        </Button>
      </div>
      <SaveBar visible={dirty || saving} onSave={save} onDiscard={discard} saving={saving} />
    </Card>
  );
}

export function SettingsPage() {
  return (
    <div className="max-w-2xl space-y-6 pb-24">
      <div>
        <h1 className="text-h1 text-text-primary mb-1">Server Configuration</h1>
        <p className="text-small text-text-secondary">Prefix, translation, and broadcast preferences.</p>
      </div>

      <PrefixCard />

      <ModuleTogglePanel module="translation" title="Translation" masterLabel="Enable translation" permissionNote="Manage Server permission required." />

      <BroadcastOptInCard />
    </div>
  );
}

/**
 * announcement_settings is a GLOBAL, single-row, bot-owner-only table
 * (docs/V18_ARCHITECTURE_NOTES.md / bridge/config_registry.py's module
 * docstring) — it is deliberately NOT in the guild configuration registry,
 * so this stays a disabled preview rather than a fake per-guild save.
 */
function BroadcastOptInCard() {
  return (
    <Card>
      <CardHeader title="Broadcasts" description="Whether this server receives occasional messages from the bot owner." />
      <div className="opacity-60">
        <ToggleRow title="Receive broadcasts" checked onChange={() => {}} disabled />
      </div>
      <p className="text-small text-text-tertiary mt-2">
        This is bot-wide, owner-only configuration, not a per-server setting — it belongs in a future Owner Console, not here.
      </p>
    </Card>
  );
}
